/** @jsxImportSource @emotion/react */
import { ReactNode, useCallback, useMemo, useState } from 'react'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Popover from 'react-bootstrap/Popover'
import moment from 'moment'

import { useLazyGetLabMessagesQuery } from 'store/services/messages'
import { Message } from 'store/types'

interface LazyMessagesPopoverProps {
  orderId: number
  children: ReactNode
}

export const LazyMessagesPopover = ({ orderId, children }: LazyMessagesPopoverProps) => {
  const [trigger, { data, isFetching, isUninitialized }] = useLazyGetLabMessagesQuery()
  const [loaded, setLoaded] = useState(false)

  const onShow = useCallback(() => {
    if (!loaded && (isUninitialized || !data)) {
      trigger({ orderId: String(orderId) })
      setLoaded(true)
    }
  }, [loaded, isUninitialized, data, orderId, trigger])

  const content = useMemo(() => {
    if (isFetching || !data) {
      return (
        <div className="p-2">
          <small>Loading…</small>
        </div>
      )
    }

    const messages: Message[] = data || []
    return (
      <div className="p-2">
        {messages.filter((x) => x.context !== 'patient-sms').slice(0, 5).map((m) => (
          <p key={m.uuid} className="p-2 mb-3">
            {moment(m.createdAt).format('MM/DD/YYYY HH:mm a')}:<br /> {m.text}
          </p>
        ))}
      </div>
    )
  }, [data, isFetching])

  const popover = (
    <Popover>
      <Popover.Header as="h3">Recent Messages</Popover.Header>
      {content}
    </Popover>
  )

  return (
    <OverlayTrigger trigger={["hover", "focus"]} placement="left" overlay={popover}>
      <span onMouseEnter={onShow} onFocus={onShow} style={{ display: 'inline-flex' }}>
        {children}
      </span>
    </OverlayTrigger>
  )
}


